import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { apiJson, ApiError } from '../lib/api'
import { hasPacking, useAccess } from '../lib/access'
import { PairTabs, sitesTabs } from '../components/PairTabs'
import { PageHeader, PageBody, headerStyles } from '../components/PageHeader'
import { useChanged, useLayer } from '../components/layers'
import { SkeletonRows, PendingButton, useSlow, inertClass, Failed } from '../components/pending'
import { TEAM_COLOURS, fmtDay } from './TeamsPage'
import { dmy, plural } from './wages/format'
import {
  RateDraft,
  RateVersion,
  Restatement,
  STATE_LABEL,
  colourName,
  current,
  draftErrors,
  firstScheduled,
  hitsPast,
  leastUsedColour,
  parseRate,
  pastBody,
  pastTitle,
  perKg,
  period,
  takesOver,
} from './pickingJobs/rates'
import { Branch, Member, Refusal, candidateTree, candidates, resolveSelection, tickBranch, tickOf } from './pickingJobs/members'
import { LocationQrs, locationQrsPdf, locationQrsPdfName } from './pickingJobs/locationQrs'
import { Structure, kg } from './sites/structure'
import { Padlock } from './SitesPage'
import { AddSiteForm, SiteMenu, SiteSettingsDialog, SiteSummary, VarietySelect, addedToast } from './sites/SiteMenu'
import { ImportDialog } from './sites/ImportDialog'
import sm from './sites/SiteMenu.module.css'
import { BoxType, grams, tolerance } from './settings/boxTypes'
import pa from './ProductActivityPage.module.css'
import st from './SitesPage.module.css'
import o from '../components/Overlay.module.css'
import styles from './PickingJobsPage.module.css'
import { openableRow } from '../lib/openableRow'
import { DateField } from '../components/DateField'
// Picking jobs (handoff §7g, Picking jobs view; CONTROL V4 jobsTab). One
// Site's jobs in Active / Inactive / Archived tabs with search; a job opens a
// slide-out panel that is a draft until Save, and its rate versions are
// written one at a time from their own dialog, straight away. Its rows are
// the same: a removal or an Add rows is its own write (POST /:id/members),
// and the server names every row it refuses. A new job takes its rows in the
// create itself (row_ids), from the create panel's picker or from the
// Structure view's "Create job from selection" (?rows=).
//
// The box type and lid (§7n.11) are chosen from General settings' lists and
// saved with the job; the type's specification is shown, never edited here,
// and links to where it is kept. Boxes per pallet is the job's own snapshot,
// taken by the server when the type is set or changed. Both are PackRight's
// (D2): on Grow the section is absent, the lists are not read and a save
// never sends them. The lid follows the product, not the Stock & Dispatch
// licence (D2 item 2).
// The list endpoint's view of a rate version: the one in force today where
// the job's site is, and the next scheduled change (routes/picking-jobs.js).
interface RateRef {
  id: string
  effective_from: string
  rate_per_kg: number
}
interface PickingJob {
  id: string
  tenant_id: string
  site_id: string
  name: string
  exact_variety: string
  colour?: string | null
  packed_at?: string | null
  marketed_by?: string | null
  distributed_by?: string | null
  active: boolean
  archived_at?: string | null
  created_at: string
  updated_at: string
  current_rate: RateRef | null
  next_rate: RateRef | null
  // Active member rows.
  rows?: number
  box_type_id?: string | null
  // The job's snapshot, taken when its box type was set or changed.
  boxes_per_pallet?: number | null
  lid_id?: string | null
  box_type_name?: string | null
}
// GET /api/settings/lids: brands (032), offered unless archived or inactive.
interface Lid {
  id: string
  name: string
  code?: string | null
  offered: boolean
}
interface JobDetail extends Omit<PickingJob, 'current_rate' | 'next_rate' | 'rows'> {
  today: string
  rates: RateVersion[]
  members?: Member[]
  // The type's specification as it stands now (routes/picking-jobs.js packingOf).
  box_type?: BoxType | null
  lid?: { id: string; name: string; code?: string | null } | null
}
type Tab = 'active' | 'inactive' | 'archived'
interface Draft {
  id: string | null
  name: string
  exact_variety: string
  colour: string
  packed_at: string
  marketed_by: string
  distributed_by: string
  active: boolean
  // '' is none.
  box_type_id: string
  lid_id: string
  // A new job only: the rows it is created with.
  row_ids: string[]
  // A new job only: its first $/kg, from the day it is made; offered from the
  // Site's default contract rate (137). '' is none yet.
  rate: string
}
// { queued, skipped_locked } from enqueueWageTargets (services/wage-basis.js).
interface WageRecalc {
  queued: number
  skipped_locked: { human_id: string; work_date: string }[]
}
const tabOf = (j: PickingJob): Tab => (j.archived_at ? 'archived' : j.active ? 'active' : 'inactive')
const TAB_LABEL: Record<Tab, string> = { active: 'Active', inactive: 'Inactive', archived: 'Archived' }
const TEXT: (keyof Draft & keyof PickingJob)[] = ['name', 'exact_variety', 'colour', 'packed_at', 'marketed_by', 'distributed_by']
function MapPin() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" aria-hidden="true">
      <path d="M8 1a4.5 4.5 0 0 0-4.5 4.5c0 2.5 4.5 8 4.5 8s4.5-5.5 4.5-8A4.5 4.5 0 0 0 8 1zm0 6.5a2 2 0 1 1 0-4 2 2 0 0 1 0 4z" />
    </svg>
  )
}
function Download() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="M7 10l5 5 5-5M12 15V3" />
    </svg>
  )
}
function Plus() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  )
}
const toDraft = (j: PickingJob | null, colour: string, rate = ''): Draft =>
  j
    ? {
        id: j.id,
        name: j.name,
        exact_variety: j.exact_variety,
        colour: (j.colour || '').toLowerCase(),
        packed_at: j.packed_at || '',
        marketed_by: j.marketed_by || '',
        distributed_by: j.distributed_by || '',
        active: j.active,
        box_type_id: j.box_type_id || '',
        lid_id: j.lid_id || '',
        row_ids: [],
        rate: '',
      }
    : { id: null, name: '', exact_variety: '', colour, packed_at: '', marketed_by: '', distributed_by: '', active: true, box_type_id: '', lid_id: '', row_ids: [], rate }
// The new job's ticked rows that are still offered for its variety: a
// variety change drops the rows it no longer matches.
const createRowIds = (s: Structure | null, d: Draft) => {
  const offered = new Set(candidates(s, d.exact_variety, null).map((c) => c.row.id))
  return d.row_ids.filter((id) => offered.has(id))
}
const refusedOf = (e: any): Refusal[] | null => (e instanceof ApiError && e.body?.code === 'MEMBERSHIP_REFUSED' ? e.body.refused || [] : null)
// What the server reports it queued, and which locked days kept their basis
// (§7j.3c). Nothing is said when nothing was priced.
function recalcLine(w: WageRecalc | undefined): string {
  if (!w) return ''
  const parts = []
  if (w.queued) parts.push(`${plural(w.queued, 'worker-day', 'worker-days')} queued for wage recalculation`)
  const locked = w.skipped_locked?.length || 0
  if (locked) parts.push(`${plural(locked, 'locked worker-day', 'locked worker-days')} ${locked === 1 ? 'keeps its' : 'keep their'} basis`)
  return parts.length ? ` ${parts.join('; ')}.` : ''
}
export const PickingJobsPage: React.FC = () => {
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()
  const [jobs, setJobs] = useState<PickingJob[] | null>(null)
  const [sites, setSites] = useState<SiteSummary[]>([])
  // The first load draws skeleton rows (h 3994-4002); a reload after a save
  // keeps the rows on screen.
  const loading = !jobs
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>('active')
  const [q, setQ] = useState('')
  const [draft, setDraft] = useState<Draft | null>(null)
  const [detail, setDetail] = useState<JobDetail | null>(null)
  const [rate, setRate] = useState<RateDraft | null>(null)
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState('')
  const [structure, setStructure] = useState<Structure | null>(null)
  const [structureError, setStructureError] = useState('')
  const [addingRows, setAddingRows] = useState(false)
  const [refused, setRefused] = useState<Refusal[]>([])
  const [selectionNote, setSelectionNote] = useState('')
  const [boxTypes, setBoxTypes] = useState<BoxType[] | null>(null)
  const [lids, setLids] = useState<Lid[]>([])
  const [packingError, setPackingError] = useState('')
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const userRef = useRef(user)
  userRef.current = user
  const uid = user?.uid
  const load = useCallback(async () => {
    const u = userRef.current
    if (!u) return
    setError(null)
    try {
      const [j, s] = await Promise.all([apiJson<PickingJob[]>(u, '/api/picking-jobs'), apiJson<SiteSummary[]>(u, '/api/sites')])
      setJobs(j)
      setSites(s)
    } catch (e: any) {
      setError(e?.message || 'Could not load the picking jobs')
      setJobs((x) => x || [])
    }
  }, [uid])
  useEffect(() => {
    load()
  }, [load])
  // The box types and lids a job may choose (General settings), PackRight's
  // alone.
  const packing = hasPacking(useAccess())
  useEffect(() => {
    const u = userRef.current
    if (!u || !packing) return
    Promise.all([apiJson<BoxType[]>(u, '/api/settings/box-types'), apiJson<Lid[]>(u, '/api/settings/lids')])
      .then(([t, l]) => {
        setBoxTypes(t)
        setLids(l)
        setPackingError('')
      })
      .catch((e: any) => setPackingError(e?.message || 'Could not load the box types'))
  }, [uid, packing])
  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 3600)
    return () => clearTimeout(t)
  }, [toast])
  // The Site in view: ?site=, else the first one still open.
  const siteId = params.get('site') || sites.find((s) => !s.archived_at)?.id || sites[0]?.id || ''
  const site = sites.find((s) => s.id === siteId) || null
  const all = jobs || []
  const inSite = all.filter((j) => j.site_id === siteId)
  const counts = { active: 0, inactive: 0, archived: 0 }
  for (const j of inSite) counts[tabOf(j)]++
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return inSite
      .filter((j) => tabOf(j) === tab)
      .filter((j) => !needle || [j.name, j.exact_variety, j.packed_at, j.marketed_by, j.distributed_by].some((v) => v && v.toLowerCase().includes(needle)))
  }, [inSite, tab, q])
  // The Site's rows, for the row pickers (GET /api/sites/:id/structure).
  const loadStructure = useCallback(async () => {
    const u = userRef.current
    if (!u || !siteId) return
    try {
      const s = await apiJson<Structure>(u, `/api/sites/${siteId}/structure`)
      setStructure(s)
      setStructureError('')
    } catch (e: any) {
      setStructureError(e?.message || 'Could not load the rows')
    }
  }, [uid, siteId])
  useEffect(() => {
    setStructure(null)
    loadStructure()
  }, [loadStructure])
  const siteStructure = structure && structure.site.id === siteId ? structure : null
  const siteAdded = async (s: { id: string; name: string }) => {
    await load()
    setParams({ site: s.id }, { replace: true })
    setToast(addedToast(s.name))
  }
  const loadDetail = async (id: string) => {
    try {
      setDetail(await apiJson<JobDetail>(user, `/api/picking-jobs/${id}`))
    } catch (e: any) {
      setToast(e?.message || 'Could not load the rates')
    }
  }
  const newColour = () => leastUsedColour(inSite.filter((x) => !x.archived_at).map((x) => x.colour))
  const newRate = () => (site?.default_rate_per_kg ? Number(site.default_rate_per_kg).toFixed(2) : '')
  const open = (j: PickingJob | null) => {
    setDraft(toDraft(j, newColour(), newRate()))
    setDetail(null)
    setRefused([])
    setSelectionNote('')
    if (j) loadDetail(j.id)
  }
  const close = () => {
    setDraft(null)
    setDetail(null)
    setRate(null)
    setAddingRows(false)
    setRefused([])
    setSelectionNote('')
  }
  // "Create job from selection" (the Structure view): ?rows= names the rows.
  // They are checked again here, and a new job's draft opens with them and
  // their one variety; the name is the person's to give.
  const fromRows = params.get('rows')
  useEffect(() => {
    if (!fromRows || !siteStructure || !jobs) return
    const ids = fromRows.split(',').filter(Boolean)
    const r = resolveSelection(siteStructure, ids)
    setDraft({ ...toDraft(null, newColour(), newRate()), exact_variety: r.variety, row_ids: r.rowIds })
    setDetail(null)
    setRefused([])
    setSelectionNote(
      !r.rowIds.length
        ? 'None of the selected rows can start a job now: they are no longer active and unassigned, or they are not one variety.'
        : r.dropped
          ? `${plural(r.dropped, 'selected row was', 'selected rows were')} left out: no longer active and unassigned.`
          : ''
    )
    const next = new URLSearchParams(params)
    next.delete('rows')
    setParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromRows, siteStructure, jobs])
  // Global search links here (h 465-485): ?job= opens that job on its own
  // tab, ?q= fills the search box. ?site= stays; both others are dropped.
  const linkedJob = params.get('job')
  const linkedQ = params.get('q')
  useEffect(() => {
    if (linkedJob === null && linkedQ === null) return
    if (linkedJob !== null && !jobs) return
    if (linkedQ !== null) setQ(linkedQ)
    const j = linkedJob ? jobs?.find((x) => x.id === linkedJob) : undefined
    if (j) {
      setTab(tabOf(j))
      open(j)
    }
    const next = new URLSearchParams(params)
    next.delete('job')
    next.delete('q')
    setParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedJob, linkedQ, jobs])
  const patch = (p: Partial<Draft>) => setDraft((d) => d && { ...d, ...p })
  const saved = draft?.id ? all.find((j) => j.id === draft.id) || null : null
  const replace = (j: Partial<PickingJob> & { id: string }) =>
    setJobs((js) => {
      const list = js || []
      return list.some((x) => x.id === j.id)
        ? list.map((x) => (x.id === j.id ? { ...x, ...j } : x))
        : [...list, { current_rate: null, next_rate: null, ...j } as PickingJob]
    })
  const save = async () => {
    if (!draft || busy) return
    const d = draft
    const body: Record<string, unknown> = {}
    // An existing job sends only what changed.
    for (const k of TEXT) {
      const v = String(d[k] ?? '').trim()
      const was = String(saved?.[k] ?? '').trim()
      if (!saved || (k === 'colour' ? was.toLowerCase() : was) !== v) body[k] = v || null
    }
    if (!saved || saved.active !== d.active) body.active = d.active
    for (const k of packing ? (['box_type_id', 'lid_id'] as const) : []) {
      if (saved ? (saved[k] || '') !== d[k] : d[k]) body[k] = d[k] || null
    }
    if (saved && !Object.keys(body).length) {
      close()
      return
    }
    if (!saved) {
      body.siteId = siteId
      const rowIds = createRowIds(siteStructure, d)
      if (rowIds.length) body.row_ids = rowIds
      if (d.rate.trim()) body.rate_per_kg = parseRate(d.rate)
    }
    setBusy(true)
    setRefused([])
    try {
      const job = await apiJson<PickingJob & { rate?: RateRef | null }>(user, saved ? `/api/picking-jobs/${saved.id}` : '/api/picking-jobs', { method: saved ? 'PUT' : 'POST', body })
      const { rate: firstRate, ...fields } = job
      replace(saved ? fields : { ...fields, current_rate: firstRate || null })
      if (saved) {
        setToast(`${job.name} saved`)
        close()
      } else {
        // The panel stays open on the new job, so a rate can be added or changed.
        const withRows = job.rows ? ` with ${plural(job.rows, 'row', 'rows')}` : ''
        setToast(`${job.name} created${withRows}. ${firstRate ? `${perKg(firstRate.rate_per_kg)} from ${dmy(firstRate.effective_from)}.` : 'It has no rate yet.'}`)
        setTab(tabOf(job as PickingJob))
        setDraft(toDraft(job as PickingJob, job.colour || ''))
        setSelectionNote('')
        loadDetail(job.id)
        if (job.rows) loadStructure()
      }
    } catch (e: any) {
      const r = refusedOf(e)
      if (r) {
        // Nothing was created. The refused rows are named and dropped from
        // the draft, and the rows re-read: someone else may have taken them.
        setRefused(r)
        const out = new Set(r.map((x) => x.id))
        setDraft((x) => x && { ...x, row_ids: x.row_ids.filter((id) => !out.has(id)) })
        loadStructure()
      }
      setToast(e?.message || 'The job was not saved')
    } finally {
      setBusy(false)
    }
  }
  // Membership is written straight away, like a rate version; the list's
  // Rows count and the pickers follow what the server says it now is.
  const afterMembers = (jobId: string, members: Member[], message: string) => {
    setDetail((x) => (x && x.id === jobId ? { ...x, members } : x))
    replace({ id: jobId, rows: members.filter((m) => !m.archived_at).length })
    setToast(message)
    loadStructure()
  }
  const removeRow = async (m: Member) => {
    if (!saved || busy) return
    setBusy(true)
    setRefused([])
    try {
      const r = await apiJson<{ members: Member[] }>(user, `/api/picking-jobs/${saved.id}/members`, { method: 'POST', body: { remove: [m.id] } })
      afterMembers(saved.id, r.members, `${m.path} removed from ${saved.name}`)
    } catch (e: any) {
      const r = refusedOf(e)
      if (r) {
        setRefused(r)
        loadDetail(saved.id)
      }
      setToast(e?.message || 'The row was not removed')
    } finally {
      setBusy(false)
    }
  }
  const archive = async () => {
    if (!saved || busy) return
    const archived = !saved.archived_at
    setBusy(true)
    try {
      const job = await apiJson<PickingJob>(user, `/api/picking-jobs/${saved.id}`, { method: 'PUT', body: { archived } })
      replace(job)
      setToast(archived ? `${job.name} archived` : `${job.name} restored`)
    } catch (e: any) {
      setToast(e?.message || 'The job was not changed')
    } finally {
      setBusy(false)
    }
  }
  // After a rate write: the panel's versions and the list's current and next.
  const afterRate = async (jobId: string, message: string) => {
    setRate(null)
    setToast(message)
    await loadDetail(jobId)
    load()
  }
  return (
    <>
      <PageHeader
        title="Sites & jobs"
        tabs={<PairTabs label="Sites and jobs" tabs={sitesTabs(siteId)} current="Picking jobs" />}
        actions={
          <button type="button" className={headerStyles.btn} disabled={!site || !!site.archived_at} onClick={() => setImportOpen(true)}>
            <Download /> Import
          </button>
        }
      >
        {sites.length > 0 && (
          <SiteMenu
            user={user}
            sites={sites}
            siteId={siteId}
            onPick={(id) => setParams({ site: id }, { replace: true })}
            onAdded={siteAdded}
            onSettings={() => setSettingsOpen(true)}
            onVarietiesChanged={loadStructure}
          />
        )}
      </PageHeader>
      {error && (
        <div className={styles.error} role="alert">
          <p>{error}</p>
          <button onClick={load}>Retry</button>
        </div>
      )}
      {sites.length === 0 && !loading && !error ? (
        <PageBody>
          <div className={styles.empty}>
            <p>No sites yet</p>
            <p className={styles.emptyHint}>A picking job belongs to a Site. Create a site first.</p>
            <AddSiteForm user={user} className={sm.emptyAdd} onAdded={siteAdded} />
          </div>
        </PageBody>
      ) : (
        <PageBody>
          <div className={`${pa.card} ${styles.cardContainer}`}>
            <div className={pa.cardHead}>
              <div className={styles.filterRow}>
                <div className={styles.filterPills}>
                  {(['active', 'inactive', 'archived'] as Tab[]).map((k) => (
                    <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? styles.filterPillActive : styles.filterPill} onClick={() => setTab(k)}>
                      {TAB_LABEL[k]} <span className={styles.filterCount}>{!loading && counts[k]}</span>
                    </button>
                  ))}
                </div>
                <div className={styles.headerControls}>
                  <input className={styles.searchInput} placeholder="Job or variety" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search jobs" />
                  <button
                    type="button"
                    className={headerStyles.btnPrimary}
                    onClick={() => open(null)}
                    disabled={!site || !!site.archived_at || loading}
                    title={site?.archived_at ? 'This site is archived and takes no new jobs' : undefined}
                  >
                    <Plus /> New job
                  </button>
                </div>
              </div>
            </div>
            <div className={styles.table} role="table" aria-label="Picking jobs">
              <div className={styles.headRow} role="row">
                {['Job', 'Exact product / variety', 'Rows', 'Current $/kg', 'Scheduled change', 'Status'].map((h) => (
                  <span key={h} role="columnheader">
                    {h}
                  </span>
                ))}
              </div>
              {loading ? (
                <SkeletonRows rowClass={styles.row} cols={6} label="Loading picking jobs" />
              ) : rows.length === 0 ? (
                <div className={pa.empty}>
                  {q.trim() ? 'No job matches that search.' : tab === 'active' ? 'No active jobs in this Site yet.' : 'Nothing here.'}
                </div>
              ) : (
                rows.map((j) => {
                  const { current_rate: cur, next_rate: next } = j
                  return (
                    <div key={j.id} role="row" data-testid="job-row" className={j.active && !j.archived_at ? styles.row : styles.rowOff} {...openableRow(() => open(j))}>
                      <div className={styles.jobCell}>
                        <span className={styles.swatch} style={{ background: j.colour || 'var(--dp-inset)' }} title={!j.colour ? 'No colour' : TEAM_COLOURS.find((c) => c.hex === j.colour!.toLowerCase())?.name || j.colour} />
                        <div className={styles.jobInfo}>
                          <div className={styles.name}>{j.name}</div>
                          <div className={styles.permanent}></div>
                        </div>
                      </div>
                      <span className={styles.cell}>{j.exact_variety}</span>
                      <div className={j.rows ? styles.cellRows : styles.cellRowsNone} data-testid="job-rows">
                        <div>{j.rows || 'None'}</div>
                        <div className={styles.plantCount}></div>
                      </div>
                      {/* "A job whose only version is in the future has no current rate and says so" (§7g). */}
                      <div>
                        <div className={cur ? styles.rate : styles.rateNone}>{cur ? `$${Number(cur.rate_per_kg).toFixed(2)}` : 'No current rate'}</div>
                        <div className={styles.rateNote}>{cur ? `from ${dmy(cur.effective_from)}` : next ? 'first rate starts later' : 'No rate set'}</div>
                      </div>
                      <span className={next ? styles.cell : styles.cellNone}>{next ? `$${Number(next.rate_per_kg).toFixed(2)} from ${dmy(next.effective_from)}` : '—'}</span>
                      <span className={styles[`status_${tabOf(j)}`]}>{TAB_LABEL[tabOf(j)]}</span>
                    </div>
                  )
                })
              )}
            </div>
            <div className={styles.foot}>
              {!loading && (
                <span>
                  {plural(rows.length, 'job', 'jobs')} in {site?.name || 'this Site'}
                </span>
              )}
            </div>
          </div>
        </PageBody>
      )}
      {draft && (
        <JobPanel
          draft={draft}
          saved={saved}
          siteName={site?.name || ''}
          defaultRate={site?.default_rate_per_kg ?? null}
          detail={detail && detail.id === draft.id ? detail : null}
          busy={busy}
          patch={patch}
          onClose={close}
          onSave={save}
          onArchive={archive}
          onRate={setRate}
          structure={siteStructure}
          structureError={structureError}
          boxTypes={boxTypes}
          lids={lids}
          packingError={packingError}
          packing={packing}
          refused={refused}
          selectionNote={selectionNote}
          onRemoveRow={removeRow}
          onAddRows={() => {
            setRefused([])
            setAddingRows(true)
          }}
        />
      )}
      {addingRows && saved && (
        <AddRowsDialog
          user={user}
          job={saved}
          siteName={site?.name || ''}
          structure={siteStructure}
          onClose={() => setAddingRows(false)}
          onStale={loadStructure}
          onDone={(members, message) => {
            setAddingRows(false)
            afterMembers(saved.id, members, message)
          }}
        />
      )}
      {rate && detail && (
        <RateDialog
          user={user}
          job={detail}
          draft={rate}
          setDraft={setRate}
          onClose={() => setRate(null)}
          onDone={(m) => afterRate(detail.id, m)}
        />
      )}
      {settingsOpen && siteId && (
        <SiteSettingsDialog
          user={user}
          siteId={siteId}
          onClose={() => setSettingsOpen(false)}
          onSaved={async (_s, m) => {
            setSettingsOpen(false)
            setToast(m)
            await load()
            await loadStructure()
          }}
        />
      )}
      {importOpen && structure && (
        <ImportDialog user={user} data={structure} onClose={() => setImportOpen(false)} onDone={() => {
          setImportOpen(false)
          load()
          loadStructure()
          setToast('Rows imported')
        }} />
      )}
      {toast && (
        <div className={pa.toast} role="status">
          {toast}
        </div>
      )}
    </>
  )
}

interface PanelProps {
  draft: Draft
  saved: PickingJob | null
  siteName: string
  detail: JobDetail | null
  busy: boolean
  patch: (p: Partial<Draft>) => void
  onClose: () => void
  onSave: () => void | Promise<void>
  onArchive: () => void | Promise<void>
  onRate: (d: RateDraft) => void
  structure: Structure | null
  structureError: string
  boxTypes: BoxType[] | null
  lids: Lid[]
  packingError: string
  packing: boolean
  refused: Refusal[]
  selectionNote: string
  onRemoveRow: (m: Member) => void
  onAddRows: () => void
  // The Site's default contract rate (137), named under a new job's rate.
  defaultRate?: number | null
}
function JobPanel(props: PanelProps) {
  const { draft: d, saved, siteName, detail, busy, patch, onClose, onSave, onArchive, onRate } = props
  const isNew = !saved
  const title = d.name.trim() || (isNew ? `New picking job: ${siteName}` : saved!.name)
  // A job's rows are exactly its variety, so the variety cannot change under
  // them (409 JOB_HAS_ROWS); archived rows count too.
  const held = detail?.members?.length || 0
  const varietyHeld = !!saved && held > 0 && d.exact_variety.trim() !== saved.exact_variety
  const blocked = !d.exact_variety.trim()
    ? 'Choose the exact product and variety'
    : !d.name.trim()
      ? 'Give the job a name'
      : varietyHeld
        ? `This job has ${plural(held, 'row', 'rows')} of ${saved!.exact_variety}. Remove them before changing its variety.`
        : isNew && d.rate.trim() && !(parseRate(d.rate) > 0)
          ? 'A contract rate is dollars and cents per kg, more than $0.00, or blank'
          : ''
  const versions = detail?.rates || []
  const cur = current(versions)
  const first = firstScheduled(versions)
  // The write in flight: 'save' or 'archive', so only the pressed button says so.
  const [busyOn, setBusyOn] = useState<string | null>(null)
  const panelBusy = busy || busyOn !== null
  const slow = useSlow(panelBusy)
  const wrappedSave = async () => {
    setBusyOn('save')
    try {
      await onSave()
    } finally {
      setBusyOn(null)
    }
  }
  const wrappedArchive = async () => {
    setBusyOn('archive')
    try {
      await onArchive()
    } finally {
      setBusyOn(null)
    }
  }
  // A layer (h 425-458): Escape, the scrim and the × ask first once the draft
  // has moved. No load writes into the draft (the rates and rows arrive in
  // `detail`), so it is the start as it opens; a new job's first save hands
  // it an id and its saved fields, and those are the start from there on.
  const panelEl = useRef<HTMLDivElement>(null)
  const json = JSON.stringify(d)
  const start = useRef({ id: d.id, json })
  if (start.current.id !== d.id) start.current = { id: d.id, json }
  const dirty = start.current.json !== json
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: dirty && !panelBusy, el: panelEl, modal: true, focusFirst: true, busy: panelBusy })
  return (
    <div className={o.drawerScrim} onClick={requestClose}>
      <div ref={panelEl} className={`${o.drawer} ${o.drawerWide}`} role="dialog" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className={o.drawerHead}>
          <div className={o.headText}>
            <h2 className={o.drawerTitle}>{title}</h2>
            <p className={o.drawerSub}>
              {isNew ? 'Choose the exact variety first. Only active, unassigned rows of that variety can join.' : `Picking job · ${siteName}${saved!.archived_at ? ' · Archived' : ''}`}
            </p>
          </div>
          <button type="button" className={o.close} aria-label="Close" onClick={requestClose}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.25" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className={`${o.drawerBody} ${inertClass(panelBusy, slow)}`}>
          <div className={o.toggleRow}>
            <div className={o.toggleText}>
              <div className={o.toggleTitle}>Active</div>
              <div className={o.toggleSub}>
                {d.active
                  ? 'Available for picking. Deactivating does not release rows that already have picking recorded.'
                  : 'Not available for new picking. Previously picked rows stay assigned to it.'}
              </div>
            </div>
            <button type="button" role="switch" aria-checked={d.active} aria-label="Active" className={o.switch} onClick={() => patch({ active: !d.active })} />
          </div>
          {/* "Creating a job asks for the exact variety first" (§7g). */}
          <div className={o.field}>
            <label className={o.fieldLabel} htmlFor="pj-variety">
              Exact product and variety{' '}<span className={o.req}>required</span>
            </label>
            {/* From the client's variety list (202): an unknown one is refused
                (400 UNKNOWN_VARIETY), so it cannot be typed here. */}
            <VarietySelect
              id="pj-variety"
              className={o.input}
              list={props.structure?.varieties || []}
              value={d.exact_variety}
              current={saved?.exact_variety}
              onChange={(v) => patch({ exact_variety: v })}
            />
          </div>
          <div className={o.field}>
            <label className={o.fieldLabel} htmlFor="pj-name">
              Job name{' '}<span className={o.req}>required</span>
            </label>
            <input id="pj-name" className={o.input} value={d.name} onChange={(e) => patch({ name: e.target.value })} placeholder="e.g. Block A Hayward" />
          </div>
          {isNew && (
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="pj-rate">
                Contract rate ($/kg)
              </label>
              <input id="pj-rate" className={o.input} inputMode="decimal" placeholder="No rate yet" value={d.rate} onChange={(e) => patch({ rate: e.target.value })} />
              <span className={o.fieldHint}>
                {props.defaultRate ? `This Site's default contract rate is ${perKg(props.defaultRate)}. ` : ''}
                The job's first rate, from today: the day it is made. Leave it blank to add a rate later.
              </span>
            </div>
          )}
          <div className={o.field}>
            <div className={styles.colourHead}>
              <span className={o.fieldLabel}>Colour</span>
              <span className={styles.colourName}>{colourName(d.colour)}</span>
            </div>
            <div className={styles.swatches} role="radiogroup" aria-label="Colour">
              {TEAM_COLOURS.map((c) => (
                <button
                  key={c.hex}
                  type="button"
                  role="radio"
                  aria-checked={c.hex === d.colour}
                  aria-label={c.name}
                  title={c.name}
                  className={c.hex === d.colour ? styles.swatchOn : styles.swatchPick}
                  style={{ background: c.hex }}
                  onClick={() => patch({ colour: c.hex })}
                />
              ))}
            </div>
            <span className={o.fieldHint}>
              {isNew ? 'Picked automatically as the colour least used in this Site. ' : ''}It marks the job in this list.
            </span>
          </div>
          {/* §7n.15: the three parties sit together, with what naming one
              means stated once under them. Sharing itself is not built (the
              field set and acceptance are open), so the note says these are
              names only for now rather than claiming anything is sent. */}
          <section className={styles.share} aria-labelledby="pj-share">
            <div>
              <div className={styles.sectionTitle} id="pj-share">
                Share production data
              </div>
              <div className={styles.note}>The businesses that handle this fruit after you</div>
            </div>
            <div className={styles.pickers}>
              {(
                [
                  ['packed_at', 'Packed at'],
                  ['marketed_by', 'Marketed by'],
                  ['distributed_by', 'Distributed by'],
                ] as const
              ).map(([k, label]) => (
                <div key={k} className={o.field}>
                  <label className={o.fieldLabel} htmlFor={`pj-${k}`}>
                    {label}
                  </label>
                  <input id={`pj-${k}`} className={o.input} value={d[k]} onChange={(e) => patch({ [k]: e.target.value })} />
                </div>
              ))}
            </div>
            <p className={styles.note} data-testid="pj-share-note">
              Where a business named here has its own Dataphyll subscription, naming it is meant to share this job's harvest details, weights and varieties with
              it. That sharing is not switched on yet, so for now these are names only. Nothing is shared with a business that has no subscription, and payroll,
              rates and costings are never shared.
            </p>
          </section>
          {props.packing && <Packing {...props} />}
          {isNew ? <NewJobRows {...props} /> : <JobRows {...props} />}
          {!isNew && (
            <div className={styles.section}>
              <div>
                <div className={styles.sectionTitle}>Rates</div>
                <div className={styles.note}>
                  One $/kg rate is in force at a time. A version runs from its date until the next one starts. A rate change is saved on its own, straight away.
                </div>
              </div>
              {!detail ? (
                <div className={styles.note}>Loading rates…</div>
              ) : (
                <>
                  {!cur && (
                    <div className={styles.noRate}>
                      {first ? `No rate is payable yet. The first version starts ${dmy(first.effective_from)}.` : 'No rate has been set for this job yet.'}
                    </div>
                  )}
                  {versions.length > 0 && (
                    <div className={styles.rates} role="table" aria-label="Rate versions">
                      {versions.map((v) => (
                        <div key={v.id} role="row" data-testid="rate-row" className={styles[`rate_${v.status}`]}>
                          <span className={v.status === 'current' ? styles.rateValueOn : styles.rateValue}>{perKg(v.rate_per_kg)}</span>
                          <span className={styles.ratePeriod}>{period(v.effective_from, v.effective_to)}</span>
                          <span className={styles[`chip_${v.status}`]}>{STATE_LABEL[v.status]}</span>
                          <button
                            type="button"
                            className={v.status === 'scheduled' ? styles.editBtn : styles.amendBtn}
                            onClick={() => onRate({ mode: 'edit', version: v, rate: Number(v.rate_per_kg).toFixed(2), from: v.effective_from })}
                          >
                            {v.status === 'scheduled' ? 'Edit' : 'Amend'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <button type="button" className={styles.addRate} onClick={() => onRate({ mode: 'add', version: null, rate: '', from: '' })}>
                    + Add rate
                  </button>
                </>
              )}
            </div>
          )}
        </div>
        <div className={o.drawerFoot}>
          <button type="button" className={o.ghost} disabled={panelBusy} onClick={requestClose}>
            Cancel
          </button>
          {!isNew && (
            <button type="button" className={o.ghost} disabled={panelBusy} onClick={wrappedArchive}>
              {saved!.archived_at ? 'Restore job' : 'Archive job'}
            </button>
          )}
          {blocked && <span className={o.footNote}>{blocked}</span>}
          <PendingButton type="button" className={o.primary} busy={busyOn === 'save'} disabled={!!blocked || panelBusy} pending={isNew ? 'Creating…' : 'Saving…'} onClick={wrappedSave}>
            {isNew ? 'Create job' : 'Save changes'}
          </PendingButton>
        </div>
      </div>
      {confirm}
    </div>
  )
}
// Box/pack type and lid (§7n.11). The picker offers active types, and the one
// the job already holds even if it has since been made inactive; the same for
// lids. The specification is read-only: box types are kept in one place.
function Packing({ draft: d, saved, detail, patch, boxTypes, lids, packingError }: PanelProps) {
  const types = boxTypes || []
  const heldType = saved?.box_type_id || ''
  const typeOptions = types.filter((t) => t.active || t.id === heldType || t.id === d.box_type_id)
  const chosen = types.find((t) => t.id === d.box_type_id) || null
  const kept = !!saved && !!d.box_type_id && d.box_type_id === heldType
  // What the job holds is the snapshot; the type's figure is the one a save
  // with a newly chosen type would copy.
  const spec = kept ? detail?.box_type || chosen : chosen
  const snapshot = kept ? saved!.boxes_per_pallet ?? null : null
  const heldLid = saved?.lid_id || ''
  const lidOptions = lids.filter((l) => l.offered || l.id === heldLid || l.id === d.lid_id)
  return (
    <div className={styles.section}>
      <div>
        <div className={styles.sectionTitle}>Packing</div>
        <div className={styles.note}>Required before this job's receipts can be palletised.</div>
      </div>
      {packingError && <div className={styles.noRate}>{packingError}</div>}
      <div className={styles.field}>
        <label className={styles.label} htmlFor="pj-box-type">
          Box type
        </label>
        <select id="pj-box-type" className={styles.input} value={d.box_type_id} disabled={!boxTypes} onChange={(e) => patch({ box_type_id: e.target.value })}>
          <option value="">No box type</option>
          {typeOptions.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
              {t.active ? '' : ' (inactive)'}
            </option>
          ))}
        </select>
        {boxTypes && types.length === 0 && (
          <span className={styles.note}>
            No box types yet. <Link to="/settings?tab=packing">Add one in General settings</Link>.
          </span>
        )}
      </div>
      {spec && (
        <div className={styles.inset} data-testid="box-spec">
          <div className={styles.insetText}>
            <div className={styles.insetTitle}>
              {snapshot != null ? `${snapshot} boxes per pallet on this job` : `${spec.boxes_per_pallet} boxes per pallet`}
            </div>
            <div className={styles.insetNote}>
              Net {grams(spec.net_weight_g)} · {tolerance(spec)}
            </div>
            {snapshot != null && snapshot !== spec.boxes_per_pallet && (
              <div className={styles.insetNote}>
                {spec.name} now says {spec.boxes_per_pallet}. This job keeps {snapshot} until its box type is changed.
              </div>
            )}
            {!kept && <div className={styles.insetNote}>Boxes per pallet is copied onto the job when it is saved.</div>}
            <div className={styles.insetNote}>
              <Link to="/settings?tab=packing">Box types & weights</Link>
            </div>
          </div>
        </div>
      )}
      {lidOptions.length > 0 && (
        <div className={styles.field}>
          <label className={styles.label} htmlFor="pj-lid">
            Lid
          </label>
          <select id="pj-lid" className={styles.input} value={d.lid_id} onChange={(e) => patch({ lid_id: e.target.value })}>
            <option value="">No lid</option>
            {lidOptions.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
                {l.offered ? '' : ' (archived)'}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  )
}
function Refused({ refused }: { refused: Refusal[] }) {
  if (!refused.length) return null
  return (
    <div className={styles.errorLine} role="alert">
      Nothing was saved. {refused.length === 1 ? 'This row was refused:' : 'These rows were refused:'}
      <ul className={styles.errs}>
        {refused.map((r) => (
          <li key={r.id}>
            {r.name || 'A row'}: {r.reason}
          </li>
        ))}
      </ul>
    </div>
  )
}
// The create panel's row picker: only active, unassigned rows of exactly the
// variety typed (§7g, "Creating a job asks for the exact variety first").
function NewJobRows({ draft: d, siteName, structure, structureError, refused, selectionNote, patch }: PanelProps) {
  const variety = d.exact_variety.trim()
  const cands = candidates(structure, variety, null)
  const chosen = new Set(createRowIds(structure, d))
  return (
    <div className={styles.section}>
      <div>
        <div className={styles.sectionTitle}>Rows</div>
        <div className={styles.note}>Only active, unassigned rows of exactly this variety are offered. A job can also be created without rows and given them later.</div>
      </div>
      {selectionNote && <div className={styles.noRate}>{selectionNote}</div>}
      {!variety ? (
        <div className={styles.note}>Choose the exact product and variety to see the rows it can take.</div>
      ) : !structure ? (
        <div className={styles.note}>{structureError || 'Loading rows…'}</div>
      ) : !cands.length ? (
        <div className={styles.noRate}>
          No active, unassigned rows of {variety} in {siteName}.
        </div>
      ) : (
        <>
          <RowPicker tree={candidateTree(structure.nodes, cands, structure.site?.show_level2 === false)} selected={chosen} onChange={(s) => patch({ row_ids: [...s] })} />
          <div className={styles.note}>
            {chosen.size ? `${plural(chosen.size, 'row', 'rows')} will join this job when it is created.` : 'No rows chosen.'}
          </div>
        </>
      )}
      <Refused refused={refused} />
    </div>
  )
}
// A saved job's rows, with the padlock chip where picking is recorded
// ("1,840 kg · permanent", §7g): those cannot be removed and say why.
function JobRows({ saved, detail, busy, refused, onRemoveRow, onAddRows }: PanelProps) {
  const members = detail?.members
  const archivedJob = !!saved!.archived_at
  const active = (members || []).filter((m) => !m.archived_at).length
  const { user } = useAuth()
  const [qrBusy, setQrBusy] = useState(false)
  const [qrFailed, setQrFailed] = useState('')
  // Location QRs for Dataphyll Crew: the job's card and every active row's,
  // or one row's to replace a lost card. The tab opens on the click, so no
  // popup blocker stands between the API's answer and the PDF; with no tab
  // the PDF downloads instead.
  const printQrs = async (only?: Member) => {
    setQrFailed('')
    setQrBusy(true)
    const tab = window.open('', '_blank')
    try {
      const q = await apiJson<LocationQrs>(user, `/api/picking-jobs/${saved!.id}/location-qrs`)
      const cards = only ? q.cards.filter((c) => c.id === only.id) : q.cards
      const url = URL.createObjectURL(new Blob([locationQrsPdf(q, fmtDay(new Date()), cards)], { type: 'application/pdf' }))
      if (tab) tab.location.href = url
      else {
        const a = document.createElement('a')
        a.href = url
        a.download = locationQrsPdfName(q.job.name)
        document.body.appendChild(a)
        a.click()
        a.remove()
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch (e) {
      tab?.close()
      setQrFailed(e instanceof ApiError ? e.message : 'Could not make the location QRs. Try again.')
    } finally {
      setQrBusy(false)
    }
  }
  return (
    <div className={styles.section}>
      <div>
        <div className={styles.sectionTitle}>Rows</div>
        <div className={styles.note}>
          A row belongs to one job of exactly its variety. Once picking is recorded against a row it stays in this job, even if the job is made inactive or archived.
        </div>
      </div>
      {!members ? (
        <div className={styles.note}>Loading rows…</div>
      ) : !members.length ? (
        <div className={styles.noRate}>No rows are in this job yet.</div>
      ) : (
        <div className={styles.members} role="table" aria-label="Member rows">
          {members.map((m) => (
            <div key={m.id} role="row" data-testid="member-row" className={m.archived_at ? styles.memberOff : styles.member}>
              <div className={styles.memberText}>
                <div className={styles.memberPath}>{m.path}</div>
                <div className={styles.memberMeta}>
                  {[m.plant_count != null ? plural(m.plant_count, 'plant', 'plants') : '', m.location_id || ''].filter(Boolean).join(' · ') || '—'}
                </div>
              </div>
              {m.picking_recorded ? (
                <span className={st.permanent} title="Picking recorded: permanent. It cannot be removed from this job.">
                  <Padlock /> {kg(m.picked_kg)} · permanent
                </span>
              ) : m.archived_at ? (
                <span className={styles.memberArchived}>Archived</span>
              ) : (
                <button type="button" className={styles.removeLink} disabled={busy} aria-label={`Remove ${m.path}`} onClick={() => onRemoveRow(m)}>
                  Remove
                </button>
              )}
              {!m.archived_at && !archivedJob && (
                <button type="button" className={styles.qrLink} disabled={qrBusy} aria-label={`Print the location QR for ${m.path}`} onClick={() => printQrs(m)}>
                  QR
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <Refused refused={refused} />
      <div className={styles.memberFoot}>
        <button type="button" className={styles.addRate} disabled={archivedJob || !members} onClick={onAddRows}>
          + Add rows
        </button>
        {!archivedJob && (
          <button
            type="button"
            className={styles.addRate}
            disabled={qrBusy || !members}
            title="Cards for Dataphyll Crew to scan: one for the job, one for each active row"
            onClick={() => printQrs()}
          >
            {qrBusy ? 'Making QRs…' : 'Print location QRs'}
          </button>
        )}
        {archivedJob && <span className={styles.blocked}>An archived job takes no new rows</span>}
        {members && <span className={styles.memberCount}>{plural(active, 'active row', 'active rows')}</span>}
      </div>
      {qrFailed && <div className={styles.blocked} role="alert">{qrFailed}</div>}
    </div>
  )
}
// Level 1 and level 2 branches with an eligible count; ticking one selects
// the rows eligible now, and a partly ticked branch shows as partial (§7g).
function RowPicker({ tree, selected, onChange }: { tree: Branch[]; selected: Set<string>; onChange: (s: Set<string>) => void }) {
  const toggle = (id: string) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange(next)
  }
  const rowsOf = (b: Branch) => (
    <div className={styles.pickRows}>
      {b.rows.map((c) => (
        <label key={c.row.id} className={selected.has(c.row.id) ? styles.pickRowOn : styles.pickRow} title={c.path}>
          <input type="checkbox" checked={selected.has(c.row.id)} onChange={() => toggle(c.row.id)} aria-label={c.path} />
          <span>{c.row.name}</span>
          {c.from && <span className={styles.pickFrom}>in {c.from.name}</span>}
        </label>
      ))}
    </div>
  )
  return (
    <div className={styles.picker} role="group" aria-label="Rows to add">
      {tree.map((b1) => (
        <div key={b1.node.id} className={styles.pickBranch}>
          <BranchTick branch={b1} selected={selected} onChange={onChange} />
          {/* Level 2 hidden: the rows sit under level 1. */}
          {!b1.children.length && <div className={styles.pickSub}>{rowsOf(b1)}</div>}
          {b1.children.map((b2) => (
            <div key={b2.node.id} className={styles.pickSub}>
              <BranchTick branch={b2} selected={selected} onChange={onChange} />
              {rowsOf(b2)}
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}
function BranchTick({ branch, selected, onChange }: { branch: Branch; selected: Set<string>; onChange: (s: Set<string>) => void }) {
  const ids = branch.rows.map((c) => c.row.id)
  const tick = tickOf(ids, selected)
  const n = ids.filter((id) => selected.has(id)).length
  return (
    <label className={styles.pickHead}>
      <input
        type="checkbox"
        ref={(el) => {
          if (el) el.indeterminate = tick === 'some'
        }}
        checked={tick === 'all'}
        onChange={() => onChange(tickBranch(ids, selected))}
        aria-label={branch.path}
      />
      <span className={styles.pickName}>{branch.node.name}</span>
      <span className={styles.pickCount}>{tick === 'some' ? `${n} of ${ids.length} eligible` : `${ids.length} eligible`}</span>
    </label>
  )
}
interface AddRowsProps {
  user: any
  job: PickingJob
  siteName: string
  structure: Structure | null
  onClose: () => void
  onStale: () => void
  onDone: (members: Member[], message: string) => void
}
// Add rows to a saved job. Unassigned rows are offered; rows in another job
// with no picking recorded appear only when asked for, and moving them is
// confirmed in words before reassign is sent. The server checks every row
// again and names any it refuses; then nothing is written.
function AddRowsDialog({ user, job, siteName, structure, onClose, onStale, onDone }: AddRowsProps) {
  const [includeAssigned, setIncludeAssigned] = useState(false)
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [confirm, setConfirm] = useState(false)
  const [refused, setRefused] = useState<Refusal[]>([])
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const slow = useSlow(busy)
  // A layer over the job panel (h 425-458): Escape closes this first, and asks
  // first once rows are ticked or the move is confirmed.
  const dialogEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged({ picked: [...picked], confirm })
  const { requestClose, confirm: discard } = useLayer(true, onClose, { dirty: dirty && !busy, el: dialogEl, modal: true, focusFirst: !!structure, busy })
  const cands = candidates(structure, job.exact_variety, job.id, includeAssigned)
  const offered = new Map(cands.map((c) => [c.row.id, c]))
  const selected = new Set([...picked].filter((id) => offered.has(id)))
  const moving = [...selected].map((id) => offered.get(id)!).filter((c) => c.from)
  const fromNames = [...new Set(moving.map((c) => c.from!.name))].join(', ')
  const blocked = !selected.size ? 'Choose rows to add' : moving.length && !confirm ? 'Confirm the move first' : ''
  const change = (s: Set<string>) => {
    setPicked(s)
    setConfirm(false)
    setErr('')
  }
  const submit = async () => {
    if (busy || blocked) return
    setBusy(true)
    setErr('')
    setRefused([])
    try {
      const r = await apiJson<{ added: { id: string; name: string; from_job: { name: string } | null }[]; members: Member[] }>(user, `/api/picking-jobs/${job.id}/members`, {
        method: 'POST',
        body: { add: [...selected], ...(moving.length ? { reassign: true } : {}) },
      })
      const moved = r.added.filter((a) => a.from_job).length
      onDone(r.members, `${plural(r.added.length, 'row', 'rows')} added to ${job.name}${moved ? `, ${moved} of them moved from another job` : ''}`)
    } catch (e: any) {
      const r = refusedOf(e)
      if (r) {
        setRefused(r)
        const out = new Set(r.map((x) => x.id))
        setPicked((p) => new Set([...p].filter((id) => !out.has(id))))
        setConfirm(false)
        onStale()
      } else {
        setErr(e?.message || 'The rows were not added')
      }
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className={o.scrim} onClick={requestClose}>
      <div ref={dialogEl} className={o.modal} role="dialog" aria-label={`Add rows to ${job.name}`} onClick={(e) => e.stopPropagation()}>
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>Add rows to {job.name}</h2>
          </div>
          <button type="button" className={o.close} aria-label="Close" onClick={requestClose}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.25" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className={`${o.body} ${inertClass(busy, slow)}`}>
          <p className={o.note}>
            <span className={o.noteLine}>Active rows of exactly {job.exact_variety} in {siteName}. A row with picking recorded in another job is permanent there and is not offered.</span>
          </p>
          <button type="button" aria-pressed={includeAssigned} className={`${includeAssigned ? pa.flagOn : pa.flag} ${styles.pillStart}`} disabled={busy} onClick={() => setIncludeAssigned(!includeAssigned)}>
            Include rows in other jobs
          </button>
          {!structure ? (
            <div className={o.fieldHint}>Loading rows…</div>
          ) : !cands.length ? (
            <div className={o.warn}>
              {includeAssigned ? `No other row of ${job.exact_variety} in ${siteName} can join this job.` : `No active, unassigned rows of ${job.exact_variety} in ${siteName}.`}
            </div>
          ) : (
            <RowPicker tree={candidateTree(structure.nodes, cands, structure.site?.show_level2 === false)} selected={selected} onChange={change} />
          )}
          {moving.length > 0 && (
            <label className={styles.ack}>
              <input type="checkbox" checked={confirm} disabled={busy} onChange={(e) => setConfirm(e.target.checked)} />
              <span>
                Move {plural(moving.length, 'row', 'rows')} out of {fromNames} into {job.name}
              </span>
            </label>
          )}
          <Refused refused={refused} />
        </div>
        <div className={o.foot}>
          <button type="button" className={o.ghost} disabled={busy} onClick={requestClose}>
            Cancel
          </button>
          {blocked && <span className={o.footNote}>{blocked}</span>}
          <PendingButton type="button" className={o.primary} busy={busy} disabled={!!blocked || busy} pending="Adding…" onClick={submit}>
            {selected.size ? `Add ${plural(selected.size, 'row', 'rows')}` : 'Add rows'}
          </PendingButton>
          <Failed text={err} />
        </div>
      </div>
      {discard}
    </div>
  )
}
interface RateDialogProps {
  user: any
  job: JobDetail
  draft: RateDraft
  setDraft: (d: RateDraft | null) => void
  onClose: () => void
  onDone: (message: string) => void
}
// Add, Edit (a scheduled version), Amend (current or past) and Remove. The
// server decides whether a change restates reported picking and names the
// figures; the acknowledgement is only ever sent after the viewer has seen
// them and ticked it, and any change to the inputs asks again.
function RateDialog({ user, job, draft: d, setDraft, onClose, onDone }: RateDialogProps) {
  const [pending, setPending] = useState<{ restatement: Restatement; acknowledgement: string; message: string } | null>(null)
  const [ack, setAck] = useState(false)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const slow = useSlow(busy)
  // A layer over the job panel (h 425-458): Escape closes this first, and asks
  // first once the rate or its date has been changed.
  const dialogEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged({ rate: d.rate, from: d.from })
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: dirty && !busy, el: dialogEl, modal: true, focusFirst: true, busy })
  const versions = job.rates
  const today = job.today
  const v = d.version
  const past = hitsPast(d, today)
  const errs = draftErrors(d, versions)
  const cur = current(versions)
  const title = d.mode === 'remove' ? 'Remove rate version' : d.mode === 'edit' ? (v?.status === 'scheduled' ? 'Edit rate version' : 'Amend rate version') : 'Add rate'
  const change = (p: Partial<RateDraft>) => {
    setDraft({ ...d, ...p })
    setPending(null)
    setAck(false)
    setErr('')
  }
  const submit = async () => {
    if (busy || errs.length || (pending && !ack)) return
    const acknowledged = pending && ack ? { acknowledge_restatement: true } : {}
    const base = `/api/picking-jobs/${job.id}/rates`
    setBusy(true)
    setErr('')
    try {
      if (d.mode === 'remove') {
        const r = await apiJson<{ takes_over: RateVersion | null; wage_recalc: WageRecalc }>(user, `${base}/${v!.id}`, { method: 'DELETE', body: acknowledged })
        onDone(
          `Rate version removed. ${r.takes_over ? `${perKg(r.takes_over.rate_per_kg)} from ${dmy(r.takes_over.effective_from)} covers its period.` : 'No rate covers its period.'}${recalcLine(r.wage_recalc)}`
        )
        return
      }
      const rate = parseRate(d.rate)
      const body: Record<string, unknown> =
        d.mode === 'add'
          ? { effective_from: d.from, rate_per_kg: rate }
          : {
              ...(d.from !== v!.effective_from ? { effective_from: d.from } : {}),
              ...(rate !== v!.rate_per_kg ? { rate_per_kg: rate } : {}),
            }
      const r = await apiJson<RateVersion & { wage_recalc: WageRecalc }>(user, d.mode === 'add' ? base : `${base}/${v!.id}`, {
        method: d.mode === 'add' ? 'POST' : 'PUT',
        body: { ...body, ...acknowledged },
      })
      onDone(`${perKg(r.rate_per_kg)} from ${dmy(r.effective_from)} saved.${recalcLine(r.wage_recalc)}`)
    } catch (e: any) {
      if (e instanceof ApiError && e.body?.code === 'RESTATEMENT_ACK_REQUIRED') {
        setPending({ restatement: e.body.restatement, acknowledgement: e.body.acknowledgement, message: e.body.message })
        setAck(false)
      } else {
        setErr(e?.message || 'The rate was not saved')
      }
    } finally {
      setBusy(false)
    }
  }
  const prev = v ? takesOver(versions, v) : null
  const futureNote =
    d.mode !== 'remove' && !past && !errs.length && d.from > today
      ? `Scheduled: this rate starts on ${dmy(d.from)}. ${cur ? `The current rate stays at ${perKg(cur.rate_per_kg)} until then.` : 'There is no current rate before then.'}`
      : ''
  const showWarn = past || !!pending
  const r = pending?.restatement
  const saveLabel = d.mode === 'remove' ? 'Remove version' : 'Save rate'
  return (
    <div className={o.scrim} onClick={requestClose}>
      <div ref={dialogEl} className={o.modal} role="dialog" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>{title}</h2>
            {v && <p className={o.sub}>Currently {perKg(v.rate_per_kg)} from {dmy(v.effective_from)}</p>}
          </div>
          <button type="button" className={o.close} aria-label="Close" onClick={requestClose}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.25" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className={`${o.body} ${inertClass(busy, slow)}`}>
          {d.mode !== 'remove' && (
            <div className={o.grid2}>
              <div className={o.field}>
                <label className={o.fieldLabel} htmlFor="pj-rate">
                  Rate ($/kg)
                </label>
                <input id="pj-rate" className={o.input} inputMode="decimal" disabled={busy} value={d.rate} onChange={(e) => change({ rate: e.target.value })} placeholder="0.00" />
              </div>
              <div className={o.field}>
                <label className={o.fieldLabel} htmlFor="pj-from">
                  Effective from
                </label>
                <DateField id="pj-from" className={o.input} disabled={busy} value={d.from} onChange={(e) => change({ from: e.target.value })} />
              </div>
            </div>
          )}
          {d.mode !== 'remove' && (d.rate || d.from) && errs.length > 0 && (
            <ul className={styles.errs}>
              {errs.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          )}
          {futureNote && <div className={styles.good}>{futureNote}</div>}
          {/* A never-in-force version is a plan: no warning, and the panel says so (§7g). */}
          {!showWarn && v && v.status === 'scheduled' && (
            <div className={styles.good}>
              {d.mode === 'remove'
                ? `This version was scheduled to start ${dmy(v.effective_from)} and has never been in force, so nothing was paid at it and no past report changes. ${prev ? `${perKg(prev.rate_per_kg)} from ${dmy(prev.effective_from)} simply continues.` : 'The job will have no rate scheduled after it is removed.'}`
                : 'This version has not started yet, so nothing has been paid at it. Changing or removing it affects no past report.'}
            </div>
          )}
          {showWarn && (
            <div className={styles.pastWarn} role="alert">
              <div className={styles.pastTitle}>{pastTitle(d)}</div>
              <p>{pastBody(d, versions)}</p>
              {r ? (
                <dl className={styles.figures}>
                  <dt>Period</dt>
                  <dd>{period(r.from, r.to)}</dd>
                  <dt>Priced at</dt>
                  <dd>{r.old_rate == null ? 'No rate' : perKg(r.old_rate)}</dd>
                  <dt>Repriced at</dt>
                  <dd>{r.new_rate == null ? 'No rate' : perKg(r.new_rate)}</dd>
                  <dt>Recorded</dt>
                  <dd>{r.kg_recorded.toLocaleString('en-NZ')} kg against this job in that period</dd>
                </dl>
              ) : (
                <p className={styles.pastHint}>Saving first shows the kg recorded in that period and asks you to confirm.</p>
              )}
              <p>{pending?.message || 'Payments already made are not reversed here. Reconcile the difference through the normal payroll adjustment.'}</p>
              {pending && (
                <label className={styles.ack}>
                  <input type="checkbox" disabled={busy} checked={ack} onChange={(e) => setAck(e.target.checked)} />
                  <span>{pending.acknowledgement}</span>
                </label>
              )}
            </div>
          )}
        </div>
        <div className={o.foot}>
          <button type="button" className={o.ghost} disabled={busy} onClick={requestClose}>
            Cancel
          </button>
          {d.mode === 'edit' && (
            <button type="button" className={styles.removeLink} disabled={busy} onClick={() => change({ mode: 'remove' })}>
              Remove this version
            </button>
          )}
          {d.mode === 'remove' && (
            <button type="button" className={styles.removeLink} disabled={busy} onClick={() => change({ mode: 'edit', rate: Number(v!.rate_per_kg).toFixed(2), from: v!.effective_from })}>
              Back to editing
            </button>
          )}
          <PendingButton
            type="button"
            className={d.mode === 'remove' ? o.dangerBtn : o.primary}
            busy={busy}
            disabled={busy || errs.length > 0 || (!!pending && !ack)}
            pending={d.mode === 'remove' ? 'Removing…' : 'Saving…'}
            onClick={submit}
          >
            {saveLabel}
          </PendingButton>
          <Failed text={err} />
        </div>
      </div>
      {confirm}
    </div>
  )
}
